import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { bookingService, userService } from '../../../api/apiService';
import '../../../App.css';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

function TalentInvitations() {
  const { currentUser } = useAuth();
  const [talents, setTalents] = useState([]);
  const [talentBookings, setTalentBookings] = useState({});
  const [groupedBookings, setGroupedBookings] = useState({});
  const [expandedBookings, setExpandedBookings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserTalentsAndBookings = async () => {
      try {
        setLoading(true);
        
        // Get user profile data
        const userResponse = await userService.getProfile();
        const userData = userResponse?.data?.user;
        
        if (!userData) {
          setError('Failed to load user profile data');
          setLoading(false);
          return;
        }
        
        console.log('User profile data:', userData);
        
        // Check if talents array exists and has items
        if (!userData.talents || !Array.isArray(userData.talents) || userData.talents.length === 0) {
          setError('No talent information found in your profile');
          setLoading(false);
          return;
        }
        
        // Log each talent to check available properties
        console.log('Talents from user profile:', userData.talents.map(talent => ({
          id: talent.db_id,
          name: talent.name,
          function: talent.function,
          Funktion: talent.Funktion,
          job: talent.job,
          position: talent.position,
          allProps: Object.keys(talent)
        })));
        
        // Set the talents with enhanced function property
        const enhancedTalents = userData.talents.map(talent => ({
          ...talent,
          functionDisplay: talent.function || talent.Funktion || talent.job || talent.position || 'No Function'
        }));
        
        setTalents(enhancedTalents);
        
        // Fetch requested bookings for each talent
        const bookingsData = {};
        
        for (const talent of enhancedTalents) {
          const talentId = talent.db_id;
          if (talentId) {
            try {
              // Get requested bookings for this talent
              const bookingsResponse = await bookingService.getBookingsByTalentIdAndStatus(talentId, 'requested');
              const bookings = bookingsResponse?.data?.bookings || [];
              
              console.log(`Fetched ${bookings.length} requested bookings for talent ${talentId}`);
              
              if (bookings.length > 0) {
                bookingsData[talentId] = bookings;
              }
            } catch (err) {
              console.error(`Error fetching bookings for talent ${talentId}:`, err);
            }
          }
        }
        
        setTalentBookings(bookingsData);
        
        // Group the bookings by group ID for each talent
        const groupedByTalent = {};
        
        Object.entries(bookingsData).forEach(([talentId, bookings]) => {
          groupedByTalent[talentId] = groupBookings(bookings);
        });
        
        setGroupedBookings(groupedByTalent);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load talent and booking data');
        setLoading(false);
      }
    };

    fetchUserTalentsAndBookings();
  }, [currentUser]);

  const groupBookings = (bookingsList) => {
    const grouped = {};
  
    // First group bookings by groupId
    bookingsList.forEach((booking) => {
      // Use booking group if available, otherwise use the booking id
      const groupId = booking.group || booking.id;
  
      if (!grouped[groupId]) {
        grouped[groupId] = [];
      }
      grouped[groupId].push(booking);
    });
  
    // Sort each group by start date
    Object.keys(grouped).forEach((groupId) => {
      grouped[groupId].sort((a, b) => {
        const dateA = new Date(a.start_date);
        const dateB = new Date(b.start_date);
        return dateA - dateB;
      });
      
      // Mark the last booking in each group
      if (grouped[groupId].length > 0) {
        grouped[groupId][grouped[groupId].length - 1].isLast = true;
      }
    });
  
    return grouped;
  };

  const handleBookingStatusChange = async (booking, action) => {
    try {
      let status;
      
      if (action === 'accept') {
        status = booking.option === 'option' ? 'optioned' : 'confirmed';
      } else if (action === 'reject') {
        status = 'declined request';
      }
      
      console.log(`Updating booking ${booking.id} to status: ${status}`);
      
      // Update the booking status
      await bookingService.updateBookingStatus(booking.id, status);
      
      // If rejecting, create a notification
      if (status === 'declined request') {
        // Create a notification for the booking creator
        if (booking.created_by) {
          const notificationData = {
            message: `Talent ${getTalentName(booking.talent_id)} rejected the order for ${booking.client?.label || 'Unknown Client'}. Please propose another talent.`,
            type: 'rejection',
            url: '/cs/allbooking'
          };
          
          await bookingService.sendNotification(booking.id, notificationData);
        }
      }
      
      toast.success(`Booking ${action === 'accept' ? 'accepted' : 'rejected'} successfully!`);
      
      // Update the local state
      refreshBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast.error('Failed to update booking status. Please try again.');
    }
  };
  
  const refreshBookings = async () => {
    try {
      setLoading(true);
      
      // Fetch requested bookings for each talent again
      const bookingsData = {};
      
      for (const talent of talents) {
        const talentId = talent.db_id;
        if (talentId) {
          try {
            // Get requested bookings for this talent
            const bookingsResponse = await bookingService.getBookingsByTalentIdAndStatus(talentId, 'requested');
            const bookings = bookingsResponse?.data?.bookings || [];
            
            if (bookings.length > 0) {
              bookingsData[talentId] = bookings;
            }
          } catch (err) {
            console.error(`Error fetching bookings for talent ${talentId}:`, err);
          }
        }
      }
      
      setTalentBookings(bookingsData);
      
      // Group the bookings by group ID for each talent
      const groupedByTalent = {};
      
      Object.entries(bookingsData).forEach(([talentId, bookings]) => {
        groupedByTalent[talentId] = groupBookings(bookings);
      });
      
      setGroupedBookings(groupedByTalent);
      setLoading(false);
    } catch (error) {
      console.error('Error refreshing data:', error);
      setLoading(false);
    }
  };

  const toggleBookingExpansion = (bookingId) => {
    setExpandedBookings(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy');
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Invalid date';
    }
  };

  const getTalentName = (talentId) => {
    const talent = talents.find(t => t.db_id === talentId);
    return talent ? talent.name : 'Unknown Talent';
  };

  if (loading) {
    return (
      <section>
        <div className="content-wrapper">
          <h1>Talent Requests</h1>
          <div className="loading">Loading your requests...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <div className="content-wrapper">
          <h1>Talent Requests</h1>
          <div className="error">
            <p>{error}</p>
            <p>Please try refreshing the page or contact support if the problem persists.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="content-wrapper">
        <h1>Talent Requests</h1>
      </div>
      
      {talents.length === 0 ? (
        <div className="content-wrapper">
          <p>No talent profiles found in your account.</p>
        </div>
      ) : (
        talents.map(talent => {
          const talentId = talent.db_id;
          const hasTalentBookings = talentBookings[talentId] && talentBookings[talentId].length > 0;
          
          return (
            <div key={talentId}>
              <div className="content-wrapper">
                <h2 style={{ marginTop: '70px', marginBottom: '50px' }}>
                  {talent.name} - {talent.functionDisplay}
                </h2>
              </div>
              
              {!hasTalentBookings ? (
                <div className="content-wrapper">
                  <p>No booking requests for this talent.</p>
                </div>
              ) : (
                <div className='content-wrapper content-wrapper-talent-list'>
                  {Object.entries(groupedBookings[talentId] || {}).map(([groupId, groupedBooking]) => 
                    groupedBooking.map((booking) => (
                      <div 
                        className='list-tasks-item-container'
                        key={booking.id}
                      >
                        <div 
                          className='tlist-preview-row'
                          onClick={() => toggleBookingExpansion(booking.id)}
                        >
                          <div className='tlist-preview-date'>
                            {formatDate(booking.start_date)}
                          </div>
                          <div className='tlist-preview-client'>
                            {booking.client?.label || 'No Client'}
                          </div>
                          <div className='tlist-preview-arrow'>
                            {expandedBookings[booking.id] ? '↑' : '↓'}
                          </div>
                        </div>

                        {expandedBookings[booking.id] && (
                          <div className='tlist-expanded-content'>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Status</div>
                              <div>{booking.status}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Date Range</div>
                              <div>{formatDate(booking.start_date)} - {formatDate(booking.end_date)}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Time</div>
                              <div>{booking.start_time || 'N/A'} - {booking.end_time || 'N/A'}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>KPI</div>
                              <div>{booking.kpi || 'N/A'}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Set</div>
                              <div>{booking.set || 'N/A'}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Position</div>
                              <div>{booking.position?.label || 'N/A'}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Option</div>
                              <div>{booking.option || 'N/A'}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Location</div>
                              <div>{booking.location?.name || 'N/A'}</div>
                            </div>

                            {booking.isLast && (
                              <div className='tlist-actions'>
                                {booking.option === 'option'
                                  ? <button className='button' onClick={() => handleBookingStatusChange(booking, 'accept')}>Accept First Option</button>
                                  : <button className='button' onClick={() => handleBookingStatusChange(booking, 'accept')}>Accept Confirmation</button>
                                }
                                <button className='button' onClick={() => handleBookingStatusChange(booking, 'reject')}>Deny</button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </section>
  );
}

export default TalentInvitations; 